import React, { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useSessionStore, isTokenExpired } from '../stores/sessionStore';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppLayout } from '../layouts/AppLayout';
import { LoginPage } from '../pages/LoginPage';
import { ForgotPasswordPage } from '../pages/ForgotPasswordPage';
import { ResetPasswordPage } from '../pages/ResetPasswordPage';
import { CampaignsPage } from '../pages/CampaignsPage';
import { useAuthBootstrap } from '../features/bootstrap/bootstrapHooks';
import { ModuleGuard } from '../components/ModuleGuard';
import { PrivacyPolicyPage, TermsOfServicePage } from '../pages/LegalPages';

// Error Boundary suave para capturar erros sem recargas em loop
// Error Boundary anti-loop para capturar erros e permitir recuperação limpa
class ChunkErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; errorMessage?: string }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, errorMessage: error?.message || 'Erro inesperado' };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ChunkErrorBoundary] Erro capturado:', error, info);
    
    // Proteção contra reload em loop: só tenta auto-recuperar 1 vez por sessão
    const msg = error?.message || '';
    const isChunk = msg.includes('dynamically imported') ||
                    msg.includes('Importing a module script failed') ||
                    msg.includes('Failed to fetch dynamically imported module');

    if (isChunk) {
      const reloadKey = 'chunk_reload_attempt_' + window.location.pathname;
      const lastAttempt = sessionStorage.getItem(reloadKey);
      if (!lastAttempt) {
        sessionStorage.setItem(reloadKey, '1');
        window.location.reload();
      }
    }
  }

  handleForceReset = () => {
    try {
      sessionStorage.clear();
      localStorage.removeItem('crm_campaigns_view_mode');
    } catch {}
    window.location.href = window.location.pathname + '?v=' + Date.now();
  };

  render() {
    if (this.state.hasError) {
      const msg = this.state.errorMessage || '';
      const isChunkError = msg.includes('dynamically imported') ||
                           msg.includes('Importing a module script failed') ||
                           msg.includes('Failed to fetch dynamically imported module');

      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', padding: '2rem', textAlign: 'center' }}>
          <div style={{ background: '#18181b', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '2.5rem', maxWidth: '520px', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
            <h3 style={{ color: '#fff', fontSize: '1.25rem', marginBottom: '0.75rem' }}>
              {isChunkError ? 'Nova versão do sistema disponível' : 'Aviso do Sistema'}
            </h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '1.5rem', lineHeight: 1.5 }}>
              {isChunkError
                ? 'Os arquivos do sistema foram atualizados. Clique abaixo para atualizar o cache e continuar.'
                : (this.state.errorMessage || 'Ocorreu um problema ao exibir esta seção.')}
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button 
                onClick={() => {
                  this.setState({ hasError: false });
                }}
                className="primary-button"
                style={{ padding: '0.75rem 1.25rem' }}
              >
                Continuar Navegando
              </button>
              <button 
                onClick={this.handleForceReset}
                className="secondary-button"
                style={{ padding: '0.75rem 1.25rem' }}
              >
                Limpar Cache e Recarregar
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Helper para tratar graciosamente imports lazy com auto-recuperação
function safeLazy<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T } | any>
) {
  return lazy(async () => {
    try {
      const mod = await importFn();
      if (mod?.default) return { default: mod.default };
      return { default: mod };
    } catch (err: any) {
      console.warn('[App] Erro no carregamento lazy, tentando reimportar:', err?.message);
      // Tentativa de retry único
      try {
        const modRetry = await importFn();
        if (modRetry?.default) return { default: modRetry.default };
        return { default: modRetry };
      } catch (retryErr: any) {
        console.error('[App] Falha permanente ao carregar módulo da página:', retryErr);
        throw retryErr;
      }
    }
  });
}

// --- Lazy-loaded pages (code-split per route) ---
// --- v2 Agentic Pages ---
const DashboardPage = safeLazy(() => import('../features/dashboard/DashboardV2').then(m => ({ default: m.DashboardV2 })));
const LeadsPage = safeLazy(() => import('../pages/LeadsPage').then(m => ({ default: m.LeadsPage })));
const ConversationsPage = safeLazy(() => import('../pages/ConversationsPage').then(m => ({ default: m.ConversationsPage })));

// --- Legacy/Other Pages ---
const ContactsPage = safeLazy(() => import('../pages/ContactsPage').then(m => ({ default: m.ContactsPage })));
const DealsPage = safeLazy(() => import('../pages/DealsPage').then(m => ({ default: m.DealsPage })));
const PipelinesPage = safeLazy(() => import('../pages/PipelinesPage').then(m => ({ default: m.PipelinesPage })));
const TasksPage = safeLazy(() => import('../pages/TasksPage').then(m => ({ default: m.TasksPage })));
const BillingsPage = safeLazy(() => import('../pages/BillingsPage').then(m => ({ default: m.BillingsPage })));
const ReportsPage = safeLazy(() => import('../pages/ReportsPage').then(m => ({ default: m.ReportsPage })));
const AiUsageReportPage = safeLazy(() => import('../pages/AiUsageReportPage').then(m => ({ default: m.default })));
const AiPackagesPage = safeLazy(() => import('../pages/AiPackagesPage').then(m => ({ default: m.default })));
const SettingsPage = safeLazy(() => import('../pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const CompaniesPage = safeLazy(() => import('../pages/CompaniesPage').then(m => ({ default: m.CompaniesPage })));
const AutomationsPage = safeLazy(() => import('../pages/AutomationsPage').then(m => ({ default: m.AutomationsPage })));
const KnowledgeBasePage = safeLazy(() => import('../pages/KnowledgeBasePage').then(m => ({ default: m.KnowledgeBasePage })));
const TagsPage = safeLazy(() => import('../pages/TagsPage').then(m => ({ default: m.TagsPage })));
const SchedulesPage = safeLazy(() => import('../pages/SchedulesPage').then(m => ({ default: m.SchedulesPage })));
const FlowBuilderPage = safeLazy(() => import('../pages/FlowBuilderPage').then(m => ({ default: m.FlowBuilderPage })));
const InformationalPage = safeLazy(() => import('../pages/InformationalPage').then(m => ({ default: m.InformationalPage })));
const CustomFieldsPage = safeLazy(() => import('../pages/CustomFieldsPage').then(m => ({ default: m.CustomFieldsPage })));
const LeadSourcesPage = safeLazy(() => import('../pages/LeadSourcesPage').then(m => ({ default: m.LeadSourcesPage })));
const WebhooksPage = safeLazy(() => import('../pages/WebhooksPage').then(m => ({ default: m.WebhooksPage })));
const ChannelsPage = safeLazy(() => import('../pages/ChannelsPage').then(m => ({ default: m.ChannelsPage })));
const SocialOpportunitiesPage = safeLazy(() => import('../pages/SocialOpportunitiesPage').then(m => ({ default: m.SocialOpportunitiesPage })));
const SocialContentPage = safeLazy(() => import('../pages/SocialContentPage').then(m => ({ default: m.SocialContentPage })));
const ScheduledActionsPage = safeLazy(() => import('../pages/ScheduledActionsPage').then(m => ({ default: m.ScheduledActionsPage })));
const ProfilePage = safeLazy(() => import('../pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const AgentsPage = safeLazy(() => import('../pages/AgentsPage').then(m => ({ default: m.default })));
const ExternalIntegrationsPage = safeLazy(() => import('../pages/ExternalIntegrationsPage').then(m => ({ default: m.default })));
const WidgetsPage = safeLazy(() => import('../pages/WidgetsPage').then(m => ({ default: m.default })));
const AdminConsolePage = safeLazy(() => import('../pages/AdminConsolePage').then(m => ({ default: m.default })));
const SubscriptionsPage = safeLazy(() => import('../pages/SubscriptionsPage').then(m => ({ default: m.SubscriptionsPage })));
const ExpenseApprovalsPage = safeLazy(() => import('../pages/ExpenseApprovalsPage'));
const SocialHealthPage = safeLazy(() => import('../pages/SocialHealthPage'));
const IncidentsPage = safeLazy(() => import('../pages/IncidentsPage'));
const GrowthDashboardPage = safeLazy(() => import('../pages/GrowthDashboardPage'));
const GrowthDiagnosticsPage = safeLazy(() => import('../pages/GrowthDiagnosticsPage'));
const GrowthExperimentsPage = safeLazy(() => import('../pages/GrowthExperimentsPage'));
const GrowthApprovalsPage = safeLazy(() => import('../pages/GrowthApprovalsPage'));
const GrowthPoliciesPage = safeLazy(() => import('../pages/GrowthPoliciesPage'));
const LeadDiscoveryPage = safeLazy(() => import('../pages/LeadDiscoveryPage'));
const DeployRequestsPage = safeLazy(() => import('../pages/DeployRequestsPage'));
const OnboardingWizardPage = safeLazy(() => import('../pages/OnboardingWizardPage').then(m => ({ default: m.default })));

function PageFallback() {
  return <div className="screen-state-inline">Carregando página...</div>;
}

function RouteBoundary({ children }: { children: React.ReactNode }) {
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<PageFallback />}>
        {children}
      </Suspense>
    </ChunkErrorBoundary>
  );
}

export function App() {
  useEffect(() => {
    useSessionStore.getState().hydrate();
  }, []);

  // Active session watcher on focus, visibilitychange, and periodic interval
  useEffect(() => {
    const checkSessionExpiry = () => {
      const state = useSessionStore.getState();
      const token = state.session?.access_token;
      if (token && isTokenExpired(token)) {
        console.warn('[App] Token expirado detectado em verificação periódica/foco. Limpando sessão.');
        state.clear();
      }
    };

    window.addEventListener('focus', checkSessionExpiry);
    document.addEventListener('visibilitychange', checkSessionExpiry);
    const interval = setInterval(checkSessionExpiry, 30_000);

    return () => {
      window.removeEventListener('focus', checkSessionExpiry);
      document.removeEventListener('visibilitychange', checkSessionExpiry);
      clearInterval(interval);
    };
  }, []);

  const isPublicLegalPage = typeof window !== 'undefined' && (
    window.location.pathname.includes('politica-de-privacidade') ||
    window.location.pathname.includes('termos-de-servico') ||
    window.location.pathname.includes('privacy') ||
    window.location.pathname.includes('terms')
  );

  const bootstrapQuery = useAuthBootstrap();

  if (bootstrapQuery.isLoading && !isPublicLegalPage) {
    return <div className="screen-state-full">Validando sessão...</div>;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/welcome" element={<RouteBoundary><OnboardingWizardPage /></RouteBoundary>} />
      <Route path="/onboarding" element={<RouteBoundary><OnboardingWizardPage /></RouteBoundary>} />

      {/* Rotas Públicas para Conformidade Google Ads / OAuth Brand Verification */}
      <Route path="/politica-de-privacidade.html" element={<PrivacyPolicyPage />} />
      <Route path="/politica-de-privacidade" element={<PrivacyPolicyPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/termos-de-servico.html" element={<TermsOfServicePage />} />
      <Route path="/termos-de-servico" element={<TermsOfServicePage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />

      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<RouteBoundary><DashboardPage /></RouteBoundary>} />
          <Route path="leads" element={<RouteBoundary><ModuleGuard moduleId="crm_leads"><LeadsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="conversations" element={<RouteBoundary><ModuleGuard moduleId="inbox_conversations"><ConversationsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="contacts" element={<RouteBoundary><ModuleGuard moduleId="crm_contacts"><ContactsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="deals" element={<RouteBoundary><ModuleGuard moduleId="crm_deals"><DealsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="pipelines" element={<RouteBoundary><ModuleGuard moduleId="crm_pipelines"><PipelinesPage /></ModuleGuard></RouteBoundary>} />
          <Route path="tasks" element={<RouteBoundary><ModuleGuard moduleId="ops_tasks"><TasksPage /></ModuleGuard></RouteBoundary>} />
          <Route path="billings" element={<RouteBoundary><ModuleGuard moduleId="fin_invoicing"><BillingsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="finance" element={<RouteBoundary><ModuleGuard moduleId="fin_invoicing"><BillingsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="reports" element={<RouteBoundary><ModuleGuard moduleId="analytics_reports"><ReportsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="ai-usage" element={<RouteBoundary><ModuleGuard moduleId="analytics_reports"><AiUsageReportPage /></ModuleGuard></RouteBoundary>} />
          <Route path="ai-packages" element={<RouteBoundary><AiPackagesPage /></RouteBoundary>} />
          <Route path="pacotes-ia" element={<RouteBoundary><AiPackagesPage /></RouteBoundary>} />
          <Route path="settings" element={<RouteBoundary><SettingsPage /></RouteBoundary>} />
          <Route path="companies" element={<RouteBoundary><ModuleGuard moduleId="crm_companies"><CompaniesPage /></ModuleGuard></RouteBoundary>} />
          <Route path="automations" element={<RouteBoundary><ModuleGuard moduleId="ai_automations"><AutomationsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="knowledge-base" element={<RouteBoundary><ModuleGuard moduleId="ai_knowledge_base"><KnowledgeBasePage /></ModuleGuard></RouteBoundary>} />
          <Route path="kb" element={<RouteBoundary><ModuleGuard moduleId="ai_knowledge_base"><KnowledgeBasePage /></ModuleGuard></RouteBoundary>} />
          <Route path="tags" element={<RouteBoundary><ModuleGuard moduleId="crm_tags"><TagsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="schedules" element={<RouteBoundary><ModuleGuard moduleId="ops_schedules"><SchedulesPage /></ModuleGuard></RouteBoundary>} />
          <Route path="flow-builder" element={<RouteBoundary><ModuleGuard moduleId="ai_flow_builder"><FlowBuilderPage /></ModuleGuard></RouteBoundary>} />
          <Route path="campaigns" element={<RouteBoundary><ModuleGuard moduleId="ai_campaigns"><CampaignsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="informational" element={<RouteBoundary><InformationalPage /></RouteBoundary>} />
          <Route path="custom-fields" element={<RouteBoundary><ModuleGuard moduleId="crm_custom_fields"><CustomFieldsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="lead-sources" element={<RouteBoundary><ModuleGuard moduleId="crm_lead_sources"><LeadSourcesPage /></ModuleGuard></RouteBoundary>} />
          <Route path="webhooks" element={<RouteBoundary><ModuleGuard moduleId="ext_integrations"><WebhooksPage /></ModuleGuard></RouteBoundary>} />
          <Route path="channels" element={<RouteBoundary><ModuleGuard moduleId="inbox_channels"><ChannelsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="social-opportunities" element={<RouteBoundary><ModuleGuard moduleId="inbox_social_ops"><SocialOpportunitiesPage /></ModuleGuard></RouteBoundary>} />
          <Route path="social-content" element={<RouteBoundary><ModuleGuard moduleId="ai_social_content"><SocialContentPage /></ModuleGuard></RouteBoundary>} />
          <Route path="scheduled-actions" element={<RouteBoundary><ModuleGuard moduleId="ops_tasks"><ScheduledActionsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="profile" element={<RouteBoundary><ProfilePage /></RouteBoundary>} />
          <Route path="agents" element={<RouteBoundary><ModuleGuard moduleId="ai_agents"><AgentsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="external-integrations" element={<RouteBoundary><ModuleGuard moduleId="ext_integrations"><ExternalIntegrationsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="integrations" element={<RouteBoundary><ModuleGuard moduleId="ext_integrations"><ExternalIntegrationsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="widgets" element={<RouteBoundary><ModuleGuard moduleId="ext_widgets"><WidgetsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="admin" element={<RouteBoundary><ModuleGuard adminOnly><AdminConsolePage /></ModuleGuard></RouteBoundary>} />
          <Route path="subscriptions" element={<RouteBoundary><ModuleGuard moduleId="fin_subscriptions"><SubscriptionsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="expense-approvals" element={<RouteBoundary><ModuleGuard moduleId="fin_expense_approvals"><ExpenseApprovalsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="social-health" element={<RouteBoundary><ModuleGuard moduleId="inbox_social_health"><SocialHealthPage /></ModuleGuard></RouteBoundary>} />
          <Route path="incidents" element={<RouteBoundary><ModuleGuard moduleId="ops_incidents"><IncidentsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="growth" element={<RouteBoundary><ModuleGuard moduleId="ai_growth_engine"><GrowthDashboardPage /></ModuleGuard></RouteBoundary>} />
          <Route path="growth-dashboard" element={<RouteBoundary><ModuleGuard moduleId="ai_growth_engine"><GrowthDashboardPage /></ModuleGuard></RouteBoundary>} />
          <Route path="growth-diagnostics" element={<RouteBoundary><ModuleGuard moduleId="ai_growth_engine"><GrowthDiagnosticsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="growth/diagnostics" element={<RouteBoundary><ModuleGuard moduleId="ai_growth_engine"><GrowthDiagnosticsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="growth-experiments" element={<RouteBoundary><ModuleGuard moduleId="ai_growth_engine"><GrowthExperimentsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="growth/experiments" element={<RouteBoundary><ModuleGuard moduleId="ai_growth_engine"><GrowthExperimentsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="growth-approvals" element={<RouteBoundary><ModuleGuard moduleId="ai_growth_engine"><GrowthApprovalsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="growth/approvals" element={<RouteBoundary><ModuleGuard moduleId="ai_growth_engine"><GrowthApprovalsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="growth-policies" element={<RouteBoundary><ModuleGuard moduleId="ai_growth_engine"><GrowthPoliciesPage /></ModuleGuard></RouteBoundary>} />
          <Route path="growth/policies" element={<RouteBoundary><ModuleGuard moduleId="ai_growth_engine"><GrowthPoliciesPage /></ModuleGuard></RouteBoundary>} />
          <Route path="lead-discovery" element={<RouteBoundary><ModuleGuard moduleId="ai_lead_discovery"><LeadDiscoveryPage /></ModuleGuard></RouteBoundary>} />
          <Route path="deploy-requests" element={<RouteBoundary><ModuleGuard moduleId="ops_tasks"><DeployRequestsPage /></ModuleGuard></RouteBoundary>} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
      </Route>
    </Routes>
  );
}

export default App;
